import React, { useState, useEffect } from 'react';
import { authenticatedFetch } from '../utils/auth';

const AdminUsage = () => {
  const [usageLogs, setUsageLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchUsageLogs();
  }, []);

  const fetchUsageLogs = async () => {
    try {
      const response = await authenticatedFetch('/admin/usage');
      if (response.ok) {
        const data = await response.json();
        setUsageLogs(data);
      } else {
        setError('Failed to fetch usage logs');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exportAsCSV = () => {
    if (usageLogs.length === 0) return;

    const headers = ['Timestamp', 'User ID', 'User Email', 'Address', 'Digipin', 'Action', 'Search Type'];
    const csvContent = [
      headers.join(','),
      ...usageLogs.map(record => [
        record.timestamp,
        record.user_id,
        `"${record.user_email}"`,
        `"${record.address}"`,
        record.digipin,
        record.action,
        record.search_type || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'admin_usage_logs.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getFilteredLogs = () => {
    let filtered = usageLogs;

    if (filter) {
      filtered = filtered.filter(log => 
        log.user_email?.toLowerCase().includes(filter.toLowerCase()) ||
        log.address?.toLowerCase().includes(filter.toLowerCase()) ||
        log.digipin?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(log => log.action === filterType);
    }

    return filtered;
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'lookup': return { bg: '#d4edda', color: '#155724' };
      case 'reverse': return { bg: '#d1ecf1', color: '#0c5460' };
      case 'bulk': return { bg: '#fff3cd', color: '#856404' };
      default: return { bg: '#f8f9fa', color: '#6c757d' };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading usage logs...</p>
      </div>
    );
  }

  const filteredLogs = getFilteredLogs();

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
      <h1>System Usage Logs</h1>
      
      {error && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Filter by user, address, or digipin..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ 
              padding: '8px', 
              border: '1px solid #ccc', 
              borderRadius: '4px',
              minWidth: '250px'
            }}
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ 
              padding: '8px', 
              border: '1px solid #ccc', 
              borderRadius: '4px'
            }}
          >
            <option value="all">All Actions</option>
            <option value="lookup">Lookup</option>
            <option value="reverse">Reverse</option>
            <option value="bulk">Bulk</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={exportAsCSV}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Export CSV
          </button>
          <button
            onClick={fetchUsageLogs}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <p>Showing {filteredLogs.length} of {usageLogs.length} total records</p>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Timestamp</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>User</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Address</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Digipin</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Action</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Search Type</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log, index) => {
              const actionColors = getActionColor(log.action);
              return (
                <tr key={index} style={{ borderBottom: '1px solid #f1f1f1' }}>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {formatDate(log.timestamp)}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{log.user_email}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>ID: {log.user_id}</div>
                    </div>
                  </td>
                  <td style={{ padding: '12px', maxWidth: '300px', wordWrap: 'break-word' }}>
                    {log.address || 'N/A'}
                  </td>
                  <td style={{ padding: '12px', fontfamily: 'monospace' }}>
                    {log.digipin || 'N/A'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor: actionColors.bg,
                      color: actionColors.color
                    }}>
                      {log.action}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textTransform: 'capitalize' }}>
                    {log.search_type || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredLogs.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px'
        }}>
          <p>No usage logs found matching your filters.</p>
        </div>
      )}
    </div>
  );
};

export default AdminUsage; 